/**
 * Dynamic delivery estimate update based on country context.
 * Only updates standard cases, preserves Preorder and custom metafield texts.
 */
(() => {
  const SHIPPING_SELECTOR = '.shipping-info';
  const DEFAULT_DE_TEXT = 'in 1–3 Werktagen';
  const DEFAULT_INTL_TEXT = 'in 3–5 Werktagen';

  /**
   * Checks if standard estimate text is active (not Preorder/metafield).
   * @param {HTMLElement} container - The shipping-info container
   * @returns {boolean}
   */
  function isStandardEstimateActive(container) {
    if (!container) return false;

    const text = container.textContent || '';

    const hasPreorder = text.includes('📦') || /Preorder/i.test(text);
    const hasUnknown = text.includes('⏳');
    
    // If a custom metafield is present, don't touch it.
    const hasCustomMetafield = /🚚.*Lieferung:\s*(?!in 1–3 Werktagen)/.test(text) && !text.includes('in 1–3 Werktagen');

    if (hasPreorder || hasUnknown || hasCustomMetafield) return false;

    return text.includes('Auf Lager') && (text.includes('1–3') || text.includes('1–2') || text.includes('3–5'));
  }

  /**
   * Reads current country code. Prefers Shopify global, falls back to localization.json.
   * @returns {Promise<string>} ISO code (e.g. "DE") or "DE" as fallback
   */
  async function readCountryCode() {
    // 1) Prefer Shopify global (works on preview + prod)
    if (window.Shopify && window.Shopify.country) {
      return String(window.Shopify.country).toUpperCase();
    }

    // 2) Fallback: try localization.json on the correct root (may be unavailable on shopifypreview)
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
      const res = await fetch(root + 'localization.json', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error('localization.json not ok');
      const data = await res.json();
      return (data?.country?.iso_code || 'DE').toUpperCase();
    } catch (e) {
      console.warn('[delivery-estimate] fallback to DE due to error:', e);
      return 'DE';
    }
  }

  /**
   * Returns the appropriate phrase for a country code.
   * @param {string} iso - ISO code (e.g. "DE")
   * @returns {string}
   */
  function phraseForCountry(iso) {
    return iso === 'DE' ? DEFAULT_DE_TEXT : DEFAULT_INTL_TEXT;
  }

  /**
   * Updates the delivery estimate in the container.
   */
  async function updateEstimate() {
    const container = document.querySelector(SHIPPING_SELECTOR);
    if (!container || !isStandardEstimateActive(container)) return;

    const iso = await readCountryCode();
    const phrase = phraseForCountry(iso);

    // Normalize any previous default to the correct phrase
    container.innerHTML = container.innerHTML
      .replace(/Lieferung:\s*in\s*[\d–\-]+(?:\s*Werktagen)?!?/i, `Lieferung: ${phrase}`);
  }

  /**
   * Binds event listeners for locale/market changes.
   */
  function bindListeners() {
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t && t.closest('[data-localization-form]')) updateEstimate();
    });
    document.addEventListener('shopify:section:load', updateEstimate);

    // Gentle debounce when focus returns (market dialogs, etc.)
    let debounce;
    window.addEventListener('focus', () => {
      clearTimeout(debounce);
      debounce = setTimeout(updateEstimate, 300);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    updateEstimate();
    bindListeners();
  });
})();

